import React, { FC, useState } from "react";
import { useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Form } from "@natera/form";
import { Address } from "@app/provider/profile";
import { validateZipCode } from "@app/utils";
import { messages as addressFormMessages } from "../addressForm/messages";
import MpxAddressSuggestionForm from "@app/components/forms/mpxAddressForm/mpxAddressSuggestionForm";

interface MpxAddressFormStandaloneProps {
  address?: Address;
  onSubmit: (address: Partial<Address>) => void;
  actions: React.ReactNode;
}

// standalone MPX address form component wrapper with internal state (used in guest/public/logged-in kit ordering + MP flows and 'My Account' add/edit address flow)
const MpxAddressFormStandalone: FC<MpxAddressFormStandaloneProps> = ({
  address,
  onSubmit,
  actions,
}) => {
  const intl = useIntl();

  const {
    clearErrors,
    getValidationError,
    setValidationError,
    clearValidationError,
  } = useErrorController();

  const [street1, setStreet1] = useState(address?.street1 || "");
  const [apartmentUnit, setApartmentUnit] = useState(address?.street2 || "");
  const [city, setCity] = useState(address?.city || "");
  const [state, setState] = useState(address?.state || "");
  const [zipCode, setZipCode] = useState(address?.zipCode || "");

  const handleStreet1Change = (value: string) => {
    clearValidationError("street1");
    setStreet1(value);
  };

  const handleCityChange = (value: string) => {
    clearValidationError("city");
    setCity(value);
  };

  const handleStateChange = (value: string) => {
    clearValidationError("state");
    setState(value);
  };

  const handleZipCodeChange = (value: string) => {
    clearValidationError("zipCode");
    setZipCode(value);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    clearErrors();

    const errors: Record<string, string> = {};

    // field validation
    if (!street1?.trim()) {
      errors.street1 = intl.formatMessage(
        addressFormMessages.addressFormRequired
      );
    }
    if (!city?.trim()) {
      errors.city = intl.formatMessage(addressFormMessages.addressFormRequired);
    }
    if (!state) {
      errors.state = intl.formatMessage(
        addressFormMessages.addressFormRequired
      );
    }
    const trimmedZipCode = (zipCode ?? "").trim();
    if (!trimmedZipCode) {
      errors.zipCode = intl.formatMessage(
        addressFormMessages.addressFormRequired
      );
    } else if (!validateZipCode(trimmedZipCode)) {
      errors.zipCode = intl.formatMessage(
        addressFormMessages.addressFormInvalidZipCode
      );
    }

    // set validation errors and focus on the first error field
    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, message]) => {
        setValidationError(field, message);
      });

      const firstErrorField = document.getElementById(Object.keys(errors)[0]);
      firstErrorField?.focus();
      return;
    }

    const newAddress: Partial<Address> = {
      street1: street1.trim(),
      street2: apartmentUnit?.trim() || undefined,
      city: city.trim(),
      state,
      zipCode: trimmedZipCode,
      uid: address?.uid,
    };

    onSubmit(newAddress);
  };

  return (
    <Form
      noValidate
      className="mpx-address-form-standalone"
      data-testid="mpxAddressFormStandalone"
      onSubmit={handleSubmit}
      buttons={actions}
    >
      <MpxAddressSuggestionForm
        street1={street1}
        onStreet1Change={handleStreet1Change}
        apartmentUnit={apartmentUnit}
        onApartmentUnitChange={setApartmentUnit}
        city={city}
        onCityChange={handleCityChange}
        state={state}
        onStateChange={handleStateChange}
        zipCode={zipCode}
        onZipCodeChange={handleZipCodeChange}
        getValidationError={getValidationError}
        setValidationError={setValidationError}
      />
    </Form>
  );
};

export default MpxAddressFormStandalone;
